import { Injectable } from "@angular/core";
import { AuthSite } from "../models/auth-logspace-locator.model";

/**
 * Service for managing site data in local storage.
 *
 * NOTE: Site selection is now primarily stored in the database via the API
 * (POST /sessions/site_selection). This local storage serves as a cache
 * for performance and offline scenarios.
 *
 * The authoritative site selection comes from /auth/me/ API response
 * (selectedSiteId field), which is resolved with fallback from other apps.
 */
@Injectable({ providedIn: "root" })
export class SiteStorageService {
  private readonly STORAGE_KEY = "bdp_con_site_data";
  private readonly LEGACY_COOKIE_KEY = "BDPST";

  constructor() {
    // Clean up legacy BDPST cookie on service initialization
    this.cleanupLegacyCookie();
  }

  /**
   * Reads site data from localStorage cache
   */
  readSiteData(): { sites?: AuthSite[]; selectedSiteId?: string | null } | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch {
      // Ignore parsing errors
    }
    return null;
  }

  /**
   * Stores site data in localStorage cache
   * Note: This is a local cache - the authoritative selection is in the database
   */
  storeSiteData(sites: AuthSite[], selectedSiteId: string | null) {
    const data = { sites, selectedSiteId };
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch {
      // Ignore storage errors (e.g., quota exceeded)
    }
  }

  /**
   * Clears site data from localStorage cache
   */
  clearSiteData() {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch {
      // Ignore errors
    }
    // Also clear legacy cookie if it exists
    this.cleanupLegacyCookie();
  }

  /**
   * Removes the legacy BDPST cookie if it exists
   * This provides backward compatibility during the migration period
   */
  private cleanupLegacyCookie() {
    try {
      // Check if the legacy cookie exists
      const cookie = document.cookie
        .split(";")
        .find((c) => c.trim().startsWith(`${this.LEGACY_COOKIE_KEY}=`));

      if (cookie) {
        // Clear the legacy cookie
        document.cookie = `${this.LEGACY_COOKIE_KEY}=; path=/; max-age=0`;
      }
    } catch {
      // Ignore errors
    }
  }
}
